/**
 * Ed25519 Signing and Verification
 *
 * Uses @noble/curves pure JS implementation for maximum robustness
 * and strictly deterministic behavior across all environments.
 *
 * Replaces WebCrypto to avoid browser extension interference.
 */
import { ed25519 } from '@noble/curves/ed25519.js';
import { decodeBase64, encodeBase64 } from './codec.js';
/**
 * Generate a new Ed25519 Key Pair
 */
export async function generateEd25519KeyPair() {
    const privateKey = ed25519.utils.randomSecretKey();
    const publicKey = ed25519.getPublicKey(privateKey);
    return { publicKey, privateKey };
}
/**
 * Sign a message
 */
export async function signWithEd25519(privateKey, message) {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const signature = ed25519.sign(data, privateKey);
    return encodeBase64(signature);
}
/**
 * Verify a signature
 */
export async function verifyWithEd25519(publicKey, message, signatureBase64) {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const signature = decodeBase64(signatureBase64);
    return ed25519.verify(signature, data, publicKey);
}
/**
 * Export Public Key to Raw Base64
 */
export async function exportEd25519PublicKey(key) {
    return encodeBase64(key);
}
/**
 * Import Public Key from Raw Base64
 */
export async function importEd25519PublicKey(base64) {
    return decodeBase64(base64);
}
/**
 * Derive Public Key from Private Key
 * Returns Raw Base64 Public Key
 */
export async function getEd25519PublicKeyFromPrivate(privateKey) {
    const publicKey = ed25519.getPublicKey(privateKey);
    return encodeBase64(publicKey);
}
/**
 * Import Private Key from Raw Seed (32 bytes)
 *
 * In Ed25519 (Noble), the "private key" is the 32-byte seed.
 */
export async function importEd25519PrivateKey(seed) {
    if (seed.length !== 32) {
        throw new Error(`Invalid seed length: ${seed.length}. Expected 32.`);
    }
    // Return a copy to ensure safety
    return new Uint8Array(seed);
}
//# sourceMappingURL=ed25519.js.map